/* 工具函式：通用的、與業務邏輯無關的工具函式，可以在專案的任何地方重複使用 */

import type { ProductCategory } from "@/types/products";

/**
 * HTML 字串中移除所有 HTML 標籤，並返回純文字內容
 *
 * @param htmlString - 要移除標籤的 HTML 字串
 * @returns 不含任何 HTML 標籤的純文字內容
 */
export const stripHTML = (htmlString: string) => {
  const div = document.createElement("div");
  div.innerHTML = htmlString;
  return div.textContent || div.innerText || "";
};

/**
 * 將 HTML 實體解碼為對應的字元，並返回解碼後的文字
 *
 * @param str - 包含 HTML 實體的字串
 * @returns 解碼後的字串
 */
export const decodeHTMLEntity = (str: string) => {
  const textArea = document.createElement("textarea");
  textArea.innerHTML = str;
  return textArea.value;
};

/**
 * 價錢格式化
 * @param price
 * @returns eg. $1,234
 *
 * @example
 * formatPrice(1234); // "$1,234"
 */
export const formatPrice = (price: number | string) => {
  const parsedPrice = parseInt(price.toString(), 10);
  return `$${parsedPrice.toLocaleString("zh-TW")}`;
};

/**
 * 將值轉換為列舉型別的值
 *
 * @template T - 列舉型別
 * @param enumObj - 列舉物件
 * @param value - 要轉換的值
 * @returns 如果值存在於列舉中，返回對應的列舉值；否則返回 undefined
 *
 * @example
 * enum ShippingMethodId {
 *   FLAT_RATE = "flat_rate"
 * }
 * transformToEnumValue(ShippingMethodId, "flat_rate"); // "flat_rate"
 * transformToEnumValue(ShippingMethodId, "xxx"); // undefined
 */

export const transformToEnumValue = <T extends Record<string, string>>(
  enumObj: T,
  value: unknown,
): T[keyof T] | undefined =>
  Object.values(enumObj).includes(value as T[keyof T]) ? (value as T[keyof T]) : undefined;

/**
 * 防抖函式，用於限制高頻率觸發的函式執行次數。
 *
 * @template T - 函式類型，必須是接受任意參數且無回傳值的函式。
 * @param fn - 需要防抖的目標函式。
 * @param delay - 延遲時間（毫秒），表示在最後一次呼叫後等待的時間。
 * @returns 一個防抖後的函式，該函式具有 `clear` 方法，可用於清除當前的延遲計時器。
 *
 * @example
 * const log = (message: string) => console.log(message);
 * const debouncedLog = debounce(log, 300);
 *
 * debouncedLog("Hello");
 * debouncedLog("World");
 * // 只有最後一次呼叫 "World" 會在 300 毫秒後執行。
 *
 * debouncedLog.clear(); // 清除當前的延遲計時器。
 */
export const debounce = <T extends (...args: any[]) => void>(fn: T, delay: number) => {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const debouncedFn = (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };

  debouncedFn.clear = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
  return debouncedFn;
};

/**
 * 使用 requestAnimationFrame 實現的節流函式，用於限制高頻率觸發的函式執行次數。
 * 適合用於滾動、resize 等需要與瀏覽器重繪同步的事件處理。
 *
 * @template T - 函式類型，必須是接受任意參數且無回傳值的函式。
 * @param fn - 需要節流的目標函式。
 * @returns 一個節流後的函式，該函式最多在每個瀏覽器重繪週期（約 60fps）執行一次。
 *
 * @example
 * const handleScroll = () => console.log("scrolled");
 * const throttledHandleScroll = throttleRAF(handleScroll);
 *
 * window.addEventListener("scroll", throttledHandleScroll, { passive: true });
 * // 即使滾動事件觸發 100 次/秒，handleScroll 最多只會執行約 60 次/秒
 */
export const throttleRAF = <T extends (...args: any[]) => void>(fn: T) => {
  let ticking = false;

  const throttledFn = (...args: Parameters<T>) => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        fn(...args);
        ticking = false;
      });
      ticking = true;
    }
  };

  return throttledFn;
};

/**
 * 將相對或部分路徑轉換為絕對 URL。
 *
 * 轉換邏輯：
 * 1. 以 http:// 或 https:// 開頭的絕對 URL，直接返回。
 * 2. 若為相對路徑，補上 Domain 組裝路徑後返回。
 * 3. 自動處理多餘或缺失的斜線，確保結果格式正確。
 *
 * @param url - 相對路徑或絕對 URL
 * @returns 絕對 URL
 *
 * @example
 * getAbsoluteUrl("images/a.jpg"); => "https://domain/images/a.jpg"
 */
export const getAbsoluteUrl = (url: string): string => {
  if (!url) return "";

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  // 確保域名以 / 結尾，路徑以 / 開頭
  const baseUrl = import.meta.env.VITE_HEADLESS_SITE_DOMAIN?.replace(/\/$/, "") || "";
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${baseUrl}${path}`;
};

// 重新整理頁面
export const reloadPage = (): void => {
  window.location.reload();
};

// 判斷是否為新品 (API 沒給上架時間，暫用商品類別判斷，後台需上架新品類別 slug: "new")
export const checkIsNewProduct = (categories: ProductCategory[]) => {
  return categories.some((cat) => cat.slug === "new");
};
